import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    CONSENT_LINK_QUERY_KEY,
    useConsentLinkExchange,
} from "../hooks/consent-link.queries";
import {
    clearConsentLinkToken,
    setConsentLinkToken,
} from "../services/consent-link.client";
import {
    clearPersistedConsentSession,
    loadPersistedConsentSession,
} from "../services/consent-link.session";
import { ConsentLinkSessionContext } from "./ConsentLinkSessionContext";

interface ConsentLinkSessionProviderProps {
    children: React.ReactNode;
}

export default function ConsentLinkSessionProvider({
    children,
}: ConsentLinkSessionProviderProps) {
    const queryClient = useQueryClient();

    const [intakeToken] = React.useState(() => {
        const hash = window.location.hash.replace(/^#/, "");
        return new URLSearchParams(hash).get("consent_token");
    });

    const [restoredSession] = React.useState(() => {
        if (intakeToken) {
            clearPersistedConsentSession();
            return null;
        }
        const stored = loadPersistedConsentSession();
        if (stored) setConsentLinkToken(stored.scoped_token);
        return stored;
    });

    const exchange = useConsentLinkExchange(intakeToken);
    const sessionData = restoredSession ?? exchange.data;

    const clear = React.useCallback(() => {
        clearConsentLinkToken();
        clearPersistedConsentSession();
        queryClient.removeQueries({ queryKey: [CONSENT_LINK_QUERY_KEY] });
    }, [queryClient]);

    const hasScope = React.useCallback(
        (scope: string) => (sessionData?.allowed_scopes ?? []).includes(scope),
        [sessionData?.allowed_scopes]
    );

    let error: string | null = null;
    if (!restoredSession && !exchange.data) {
        if (!intakeToken) {
            error = "This consent link is invalid.";
        } else if (exchange.isError) {
            error = "This consent link has expired or is no longer valid.";
        }
    }

    const value = React.useMemo(
        () => ({
            token: sessionData?.scoped_token ?? null,
            consentId: sessionData?.consent_id ?? "",
            allowedScopes: sessionData?.allowed_scopes ?? [],
            loading: Boolean(intakeToken) && exchange.isLoading,
            error,
            hasScope,
            clear,
        }),
        [sessionData, exchange.isLoading, intakeToken, error, hasScope, clear]
    );

    return (
        <ConsentLinkSessionContext.Provider value={value}>
            {children}
        </ConsentLinkSessionContext.Provider>
    );
}
